import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, firstValueFrom, map, Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ChatResponse, ChatsDataResponse, Client, Links, Meta3, QtyUnreadMessages, UserShortResponse } from '@api/client';
import { CHAT_TYPE, FREELANCE_CHAT_STATUS, MESSAGE_TYPE, MessageVm } from '@chat/interfaces/chat.interfaces';
import { ChatReactionNavigationSocketEvent } from '@chat/interfaces/reaction-navigation.interfaces';
import { buildChatEventPreview } from '@chat/utils/chat-list-preview.utils';
import { WebSocketController } from '@services/web-socket.controller';
import { LocalStorageService } from '@services/local-storage.service';
import { CHAT_COUNT_ON_PAGE } from '@constants/app.constants';
import {
  ChatPreviewSocketPatch,
  ChatUnreadSocketPatch,
  ChatPresenceEvent,
  ChatPresenceState,
  ChatPresenceUpdate,
  ChatTypingEvent,
  ChatTypingSnapshot,
  ChatTypingUpdate,
  EventChatCreated,
  EventMessage,
  EventRead,
  EventReplyStatusChanged,
  SOCKET_EVENT_NAMES
} from '@services/web-socket.interfaces';
import { ReplyStatus } from '@reply/reply.constants';
import { AuthService } from '@services/auth.service';
import { ChatComposerDraftPersistenceService } from '@services/chat-composer-draft-persistence.service';
import { ChatOutboxService } from '@services/chat-outbox.service';
import { ChatReplyTargetInvalidationService } from '@services/chat-reply-target-invalidation.service';

export interface ChatsListMeta {
  currentPage: number;
  perPage: number;
  lastPage: number;
  total: number;
  nextPageUrl: string | null;
  prevPageUrl: string | null;
}

export interface ChatsRequestOptions {
  type?: CHAT_TYPE;
  status?: FREELANCE_CHAT_STATUS | 'all';
  sort?: string;
  order?: 'asc' | 'desc';
}

type ChatListItem = ChatResponse & {
  has_unread_reactions?: boolean;
  reaction_navigation_unread_count?: number;
  reaction_navigation_updated_at?: string | undefined;
};

type ChatPresenceEntry = {
  isOnline: boolean;
  lastActivityAt?: string;
};

type ChatTypingEntry = {
  expiresAt: number;
  timerId: ReturnType<typeof setTimeout> | null;
};

@Injectable({
  providedIn: 'root'
})
export class ChatsService {
  #webSocketController = inject(WebSocketController);
  #client = inject(Client);
  #localStorage = inject(LocalStorageService);
  #authService = inject(AuthService);
  #chatComposerDraftPersistenceService = inject(ChatComposerDraftPersistenceService);
  #chatOutboxService = inject(ChatOutboxService);
  #chatReplyTargetInvalidationService = inject(ChatReplyTargetInvalidationService);

  public selectedChatType: CHAT_TYPE = CHAT_TYPE.ALL;
  public selectedChatStatusSegment: FREELANCE_CHAT_STATUS = FREELANCE_CHAT_STATUS.PUBLISHED;

  #chatsSubject: BehaviorSubject<ChatListItem[]> = new BehaviorSubject<ChatListItem[]>([]);
  public chats$: Observable<ChatListItem[]> = this.#chatsSubject.asObservable();

  public set chats(value: ChatListItem[]) {
    this.#chatsSubject.next(value);
  }

  public get chats(): ChatListItem[] {
    return this.#chatsSubject.value;
  }

  #unreadMessagesSubject: BehaviorSubject<QtyUnreadMessages> = new BehaviorSubject<QtyUnreadMessages>(new QtyUnreadMessages);
  public unreadMessages$: Observable<QtyUnreadMessages> = this.#unreadMessagesSubject.asObservable();

  public set unreadMessages(value: QtyUnreadMessages) {
    this.#unreadMessagesSubject.next(value);
  }

  public hasUnreadMessages$: Observable<boolean> = this.unreadMessages$.pipe(
    map((data: QtyUnreadMessages): boolean => ((data?.done || 0) + (data?.connects || 0) + (data?.inProgress || 0)) > 0)
  );

  #activeChatIdSubject: BehaviorSubject<number | null> = new BehaviorSubject<number | null>(null);
  // public activeChatId$: Observable<number | null> = this.#activeChatIdSubject.asObservable();

  public get activeChatId(): number | null {
    return this.#activeChatIdSubject.value;
  }

  #chatPresence: Map<number, Map<number, ChatPresenceEntry>> = new Map<number, Map<number, ChatPresenceEntry>>();
  #chatTyping: Map<number, Map<number, ChatTypingEntry>> = new Map<number, Map<number, ChatTypingEntry>>();
  #pendingCreatedChats: Map<number, ChatListItem> = new Map<number, ChatListItem>();
  #pendingWalletTransferMessages: Map<number, Map<string, MessageVm>> = new Map<number, Map<string, MessageVm>>();

  #reloadChatsSubject: Subject<void> = new Subject<void>();
  public reloadChats$: Observable<void> = this.#reloadChatsSubject.asObservable();

  #chatPresenceChangedSubject: Subject<number> = new Subject<number>();
  public chatPresenceChanged$: Observable<number> = this.#chatPresenceChangedSubject.asObservable();

  #chatTypingChangedSubject: Subject<number> = new Subject<number>();
  public chatTypingChanged$: Observable<number> = this.#chatTypingChangedSubject.asObservable();

  private readonly chatTypingFallbackTtlMs: number = 8_000;

  private get myUserId(): number | null {
    return this.#authService.parsedToken?.userId;
  }

  constructor() {
    this.selectedChatStatusSegment = this.#localStorage.getItem('selectedChatStatusSegment') || FREELANCE_CHAT_STATUS.PUBLISHED;
    this.selectedChatType = this.#localStorage.getItem('selectedChatType') || CHAT_TYPE.ALL;

    this.#webSocketController.chatEvent.subscribe(event => {
      switch (event.type) {
        case SOCKET_EVENT_NAMES.MESSAGE:
          if (!event.data?.projection_update) {
            this.clearTypingByMessage(event.data);
          }
          void this.handleMessageEvent(event.data);
          break;
        case SOCKET_EVENT_NAMES.REPLY_STATUS_CHANGED:
          this.handleReplyStatusChanged(event.data);
          break;
        case SOCKET_EVENT_NAMES.READ: {
          this.handleReadEvent(event.data);
          break;
        }
        case SOCKET_EVENT_NAMES.QTY_UNREAD_MESSAGES: {
          this.handleQtyUnreadMessages(event.data);
          break;
        }
        case SOCKET_EVENT_NAMES.CHAT_CREATED:
          this.handleChatCreated(event.data);
          break;
        case SOCKET_EVENT_NAMES.DATA_UPDATED:
          this.handleDataUpdatedEvent(event.data);
          break;
        case SOCKET_EVENT_NAMES.CHAT_UNREAD_UPDATED:
          this.handleChatUnreadUpdatedEvent(event.data);
          break;
        case SOCKET_EVENT_NAMES.REACTION_NAVIGATION_UPDATED:
          this.handleReactionNavigationUpdated(event.data);
          break;
      }
    });

    this.#webSocketController.chatPresenceEvent.subscribe((event: ChatPresenceEvent): void => {
      switch (event.type) {
        case SOCKET_EVENT_NAMES.CHAT_PRESENCE_INIT:
          this.initPresenceState(event.data);
          break;
        case SOCKET_EVENT_NAMES.CHAT_PRESENCE_UPDATE:
          this.updatePresenceState(event.data);
          break;
      }
    });

    this.#webSocketController.chatTypingEvent.subscribe((event: ChatTypingEvent): void => {
      switch (event.type) {
        case SOCKET_EVENT_NAMES.CHAT_TYPING_UPDATE:
          this.updateTypingState(event.data);
          break;
        case SOCKET_EVENT_NAMES.CHAT_TYPING_SNAPSHOT:
          this.updateTypingSnapshot(event.data);
          break;
      }
    });

    this.#webSocketController.isSocketInitedSubject.pipe(
      distinctUntilChanged(),
      filter(Boolean),
      filter(() => this.#authService.isAuthenticated),
    ).subscribe((): void => {
      void this.checkUnreadMessages();

      if (this.chats.length > 0) {
        this.markNeedChatsReload();
      }
    });

    this.#webSocketController.isSocketConnectSubject.pipe(
      distinctUntilChanged(),
      filter((isConnected: boolean): boolean => !isConnected),
    ).subscribe((): void => {
      this.clearChatTypingState();
    });
  }

  public addPendingWalletTransferMessage(message: MessageVm): void {
    if (!message.guid) {
      return;
    }

    const messages = this.#pendingWalletTransferMessages.get(message.chat_id) ?? new Map<string, MessageVm>();
    messages.set(message.guid, message);
    this.#pendingWalletTransferMessages.set(message.chat_id, messages);
  }

  public getPendingWalletTransferMessages(chatId: number): MessageVm[] {
    return [...(this.#pendingWalletTransferMessages.get(chatId)?.values() ?? [])];
  }

  public resolvePendingWalletTransferMessage(chatId: number, guid: string): void {
    const messages = this.#pendingWalletTransferMessages.get(chatId);
    if (!messages) {
      return;
    }

    messages.delete(guid);
    if (messages.size === 0) {
      this.#pendingWalletTransferMessages.delete(chatId);
    }
  }

  public async checkUnreadMessages(): Promise<void> {
    try {
      this.unreadMessages = await firstValueFrom(this.#client.chatQtyUnreadMessages());
    } catch (error) {
      console.error('Error while loading quantity unread messages', error);
    }
  }

  public async requestChats(
    page: number,
    countOnPage?: number,
    searchName?: string,
    options?: ChatsRequestOptions
  ): Promise<{ chats: ChatListItem[]; meta: ChatsListMeta }> {
    const limit: number = countOnPage ?? CHAT_COUNT_ON_PAGE;
    const type: CHAT_TYPE = options?.type ?? this.selectedChatType;
    const status: FREELANCE_CHAT_STATUS | 'all' = options?.status ?? this.selectedChatStatusSegment;
    const sort: string | undefined = options?.sort;
    const order: 'asc' | 'desc' = options?.order ?? 'desc';

    try {
      const response: ChatsDataResponse | undefined = await firstValueFrom(this.#client.chatIndex(
        type,
        status,
        sort,
        order,
        limit,
        page,
        searchName || undefined,
      ));

      const chats: ChatListItem[] = Array.isArray(response?.data) ? response.data : [];
      const meta: Meta3 | undefined = response?.meta;
      const links: Links | undefined = response?.links;

      const currentPage: number = meta?.current_page ?? page ?? 1;
      const perPage: number = meta?.per_page ?? limit ?? CHAT_COUNT_ON_PAGE;
      const lastPage: number = meta?.last_page ?? currentPage;
      const total: number = meta?.total ?? chats.length;
      const nextPageUrl: string | null = links?.next ?? null;
      const prevPageUrl: string | null = links?.prev ?? null;

      return {
        chats,
        meta: {
          currentPage,
          perPage,
          lastPage,
          total,
          nextPageUrl,
          prevPageUrl,
        },
      };
    } catch (error) {
      console.error('Error while requesting chats', error);
      return {
        chats: [],
        meta: {
          currentPage: page,
          perPage: limit,
          lastPage: page,
          total: 0,
          nextPageUrl: null,
          prevPageUrl: null,
        },
      };
    }
  }

  public patchChatInList(chatId: number, patch: Partial<ChatListItem>): void {
    if (!chatId) return;

    const chatsList: ChatListItem[] = this.chats;
    const chatIndex: number = chatsList.findIndex((chat: ChatListItem): boolean => chat.id === chatId);

    if (chatIndex === -1) return;

    const originalChat: ChatListItem = chatsList[chatIndex];
    const updatedChat: ChatListItem = this.cloneChat(originalChat);

    Object.assign(updatedChat, patch);

    const updatedChats: ChatListItem[] = [...chatsList];
    updatedChats[chatIndex] = updatedChat;
    this.chats = updatedChats;
  }

  public rememberCreatedChat(chat: ChatResponse | null | undefined): void {
    if (!chat?.id) return;

    this.#pendingCreatedChats.set(chat.id, this.cloneChat(chat));
  }

  public getChatPresence(chatId: number, userId: number): boolean | undefined {
    return this.#chatPresence.get(chatId)?.get(userId)?.isOnline;
  }

  public getChatLastActivityAt(chatId: number, userId: number): string | undefined {
    return this.#chatPresence.get(chatId)?.get(userId)?.lastActivityAt;
  }

  public getChatPresenceState(chatId: number, userId: number): ChatPresenceEntry | undefined {
    return this.#chatPresence.get(chatId)?.get(userId);
  }

  public getChatOnlineCount(chatId: number, excludeUserId?: number | null): number | null {
    const presenceMap = this.#chatPresence.get(chatId);
    if (!presenceMap) return null;

    let count = 0;
    presenceMap.forEach((presenceEntry, userId): void => {
      if (!presenceEntry?.isOnline) return;
      if (excludeUserId != null && userId === excludeUserId) return;
      count += 1;
    });

    return count;
  }

  public getChatTypingUserIds(chatId: number, excludeUserId?: number | null, now: number = Date.now()): number[] {
    const typingMap = this.#chatTyping.get(chatId);
    if (!typingMap) return [];

    const userIds: number[] = [];
    typingMap.forEach((typingEntry: ChatTypingEntry, userId: number): void => {
      if (!typingEntry || typingEntry.expiresAt <= now) return;
      if (excludeUserId != null && userId === excludeUserId) return;
      userIds.push(userId);
    });

    return userIds;
  }

  public isUserTypingInChat(chatId: number, userId: number, now: number = Date.now()): boolean {
    const typingEntry: ChatTypingEntry | undefined = this.#chatTyping.get(chatId)?.get(userId);
    return !!typingEntry && typingEntry.expiresAt > now;
  }

  public clearChatTypingForChat(chatId: number | null | undefined): void {
    if (typeof chatId !== 'number') return;

    const typingMap: Map<number, ChatTypingEntry> | undefined = this.#chatTyping.get(chatId);
    if (!typingMap) return;

    typingMap.forEach((typingEntry: ChatTypingEntry): void => {
      if (typingEntry.timerId) {
        clearTimeout(typingEntry.timerId);
      }
    });
    this.#chatTyping.delete(chatId);
    this.#chatTypingChangedSubject.next(chatId);
  }

  /* public getChatFromList(chatId: number): ChatResponse {
    return this.chats.find((chat: ChatResponse) => chat.id === chatId);
  }

  public removeChatFromList(chatId: number): void {
    this.chats = this.chats.filter(chat => chat.id !== chatId);
  } */

  public setActiveChatId(chatId: number | null): void {
    this.#activeChatIdSubject.next(chatId);
  }

  /* public requestChatsReload(): void {
    this.#reloadChatsSubject.next();
  } */

  private initPresenceState(presenceStates: ChatPresenceState[]): void {
    if (!presenceStates?.length) return;

    presenceStates.forEach((chatPresenceState: ChatPresenceState): void => {
      if (typeof chatPresenceState?.chatId !== 'number') return;

      const presenceMap: Map<number, ChatPresenceEntry> = new Map<number, ChatPresenceEntry>();
      chatPresenceState?.users?.forEach((presenceUser): void => {
        if (typeof presenceUser?.userId === 'number') {
          presenceMap.set(
            presenceUser.userId,
            this.mergeChatPresenceEntry(undefined, {
              isOnline: !!presenceUser.isOnline,
              lastActivityAt: presenceUser.lastActivityAt,
            }),
          );
        }
      });
      this.#chatPresence.set(chatPresenceState.chatId, presenceMap);
      this.updateChatPresence(chatPresenceState.chatId, presenceMap);
      this.#chatPresenceChangedSubject.next(chatPresenceState.chatId);
    });
  }

  private updatePresenceState(update: ChatPresenceUpdate): void {
    if (typeof update?.chatId !== 'number' || typeof update?.userId !== 'number') return;

    const presenceMap: Map<number, ChatPresenceEntry> = this.#chatPresence.get(update.chatId) || new Map<number, ChatPresenceEntry>();
    const currentEntry: ChatPresenceEntry | undefined = presenceMap.get(update.userId);
    presenceMap.set(
      update.userId,
      this.mergeChatPresenceEntry(currentEntry, {
        isOnline: !!update.isOnline,
        lastActivityAt: update.lastActivityAt,
      }),
    );
    this.#chatPresence.set(update.chatId, presenceMap);
    this.updateChatPresence(update.chatId, presenceMap);
    this.#chatPresenceChangedSubject.next(update.chatId);
  }

  private updateTypingState(update: ChatTypingUpdate): void {
    if (typeof update?.chatId !== 'number' || typeof update?.userId !== 'number') return;
    if (update.userId === this.myUserId) return;

    if (update.isTyping) {
      this.setChatTypingState(update.chatId, update.userId, update.expiresAt);
      return;
    }

    this.removeChatTypingState(update.chatId, update.userId);
  }

  private updateTypingSnapshot(snapshot: ChatTypingSnapshot): void {
    if (typeof snapshot?.chatId !== 'number' || !Array.isArray(snapshot.users)) return;

    const snapshotUserIds: Set<number> = new Set<number>();
    let hasChanges = false;
    snapshot.users.forEach((user): void => {
      if (typeof user?.userId !== 'number') return;
      if (user.userId === this.myUserId) return;

      snapshotUserIds.add(user.userId);
      hasChanges = this.setChatTypingState(snapshot.chatId, user.userId, user.expiresAt, false) || hasChanges;
    });

    const typingMap: Map<number, ChatTypingEntry> | undefined = this.#chatTyping.get(snapshot.chatId);
    if (typingMap) {
      Array.from(typingMap.entries()).forEach(([userId, typingEntry]: [number, ChatTypingEntry]): void => {
        if (snapshotUserIds.has(userId)) return;

        if (typingEntry.timerId) {
          clearTimeout(typingEntry.timerId);
        }
        typingMap.delete(userId);
        hasChanges = true;
      });

      if (typingMap.size === 0) {
        this.#chatTyping.delete(snapshot.chatId);
      }
    }

    if (hasChanges) {
      this.#chatTypingChangedSubject.next(snapshot.chatId);
    }
  }

  private setChatTypingState(chatId: number, userId: number, expiresAtValue?: string | null, notify: boolean = true): boolean {
    const expiresAt: number = this.normalizeChatTypingExpiresAt(expiresAtValue);
    const typingMap: Map<number, ChatTypingEntry> = this.#chatTyping.get(chatId) || new Map<number, ChatTypingEntry>();
    const currentEntry: ChatTypingEntry | undefined = typingMap.get(userId);

    if (currentEntry?.timerId) {
      clearTimeout(currentEntry.timerId);
    }

    const timeoutMs: number = Math.max(0, expiresAt - Date.now());
    const timerId: ReturnType<typeof setTimeout> = setTimeout((): void => {
      this.removeChatTypingState(chatId, userId);
    }, timeoutMs);

    typingMap.set(userId, {
      expiresAt,
      timerId,
    });
    this.#chatTyping.set(chatId, typingMap);

    if (!currentEntry) {
      if (notify) {
        this.#chatTypingChangedSubject.next(chatId);
      }
      return true;
    }

    return false;
  }

  private removeChatTypingState(chatId: number, userId: number): void {
    const typingMap: Map<number, ChatTypingEntry> | undefined = this.#chatTyping.get(chatId);
    const currentEntry: ChatTypingEntry | undefined = typingMap?.get(userId);
    if (!typingMap || !currentEntry) return;

    if (currentEntry.timerId) {
      clearTimeout(currentEntry.timerId);
    }
    typingMap.delete(userId);
    if (typingMap.size === 0) {
      this.#chatTyping.delete(chatId);
    }

    this.#chatTypingChangedSubject.next(chatId);
  }

  private clearTypingByMessage(data: EventMessage): void {
    if (typeof data?.chat_id !== 'number' || typeof data?.from_id !== 'number') return;
    this.removeChatTypingState(data.chat_id, data.from_id);
  }

  private clearChatTypingState(): void {
    if (!this.#chatTyping.size) return;

    const changedChatIds: number[] = Array.from(this.#chatTyping.keys());
    this.#chatTyping.forEach((typingMap: Map<number, ChatTypingEntry>): void => {
      typingMap.forEach((typingEntry: ChatTypingEntry): void => {
        if (typingEntry.timerId) {
          clearTimeout(typingEntry.timerId);
        }
      });
    });
    this.#chatTyping.clear();
    changedChatIds.forEach((chatId: number): void => {
      this.#chatTypingChangedSubject.next(chatId);
    });
  }

  private normalizeChatTypingExpiresAt(value: string | null | undefined): number {
    if (typeof value === 'string' && value.trim().length > 0) {
      const timestamp: number = Date.parse(value);
      if (!Number.isNaN(timestamp) && timestamp > Date.now()) {
        return timestamp;
      }
    }

    return Date.now() + this.chatTypingFallbackTtlMs;
  }

  private updateChatPresence(chatId: number, presenceMap: Map<number, ChatPresenceEntry>): void {
    const chatsList: ChatListItem[] = this.chats;
    const chatIndex: number = chatsList.findIndex((chat: ChatListItem): boolean => chat.id === chatId);

    if (chatIndex === -1) return;

    const originalChat: ChatListItem = chatsList[chatIndex];
    let updatedChat: ChatListItem = originalChat;
    let changed = false;

    if (!changed && (originalChat?.user_1?.id != null && presenceMap.has(originalChat.user_1.id)
      || (originalChat?.user_2?.id != null && presenceMap.has(originalChat.user_2.id)))) {
      updatedChat = this.cloneChat(originalChat);
      changed = true;
    }

    if (!changed) return;

    const updatedChats: ChatListItem[] = [...chatsList];
    updatedChats[chatIndex] = updatedChat;
    this.chats = updatedChats;
  }

  private cloneChat(chat: ChatResponse): ChatListItem {
    return Object.assign(new ChatResponse(), chat);
  }

  private consumePendingCreatedChat(chatId: number): ChatListItem | undefined {
    const chat: ChatListItem | undefined = this.#pendingCreatedChats.get(chatId);
    if (!chat) return undefined;

    this.#pendingCreatedChats.delete(chatId);
    return this.cloneChat(chat);
  }

  private mergeChatPresenceEntry(
    currentEntry: ChatPresenceEntry | undefined,
    update: { isOnline: boolean; lastActivityAt?: string | null },
  ): ChatPresenceEntry {
    const normalizedLastActivityAt = this.normalizePresenceTimestamp(update.lastActivityAt);
    const mergedLastActivityAt = this.pickLatestPresenceTimestamp(currentEntry?.lastActivityAt, normalizedLastActivityAt);

    return {
      isOnline: update.isOnline,
      lastActivityAt: mergedLastActivityAt,
    };
  }

  private pickLatestPresenceTimestamp(
    currentValue: string | undefined,
    nextValue: string | undefined,
  ): string | undefined {
    if (!currentValue) return nextValue;
    if (!nextValue) return currentValue;

    const currentTimestamp = Date.parse(currentValue);
    const nextTimestamp = Date.parse(nextValue);

    if (Number.isNaN(currentTimestamp)) return nextValue;
    if (Number.isNaN(nextTimestamp)) return currentValue;

    return nextTimestamp >= currentTimestamp ? nextValue : currentValue;
  }

  private normalizePresenceTimestamp(value: string | null | undefined): string | undefined {
    if (typeof value !== 'string') return undefined;

    const normalizedValue = value.trim();
    if (!normalizedValue) return undefined;

    const timestamp = Date.parse(normalizedValue);
    return Number.isNaN(timestamp) ? undefined : new Date(timestamp).toISOString();
  }

  private parseChatTimestamp(value: string | Date | null | undefined): number | null {
    if (typeof value === 'string') {
      const normalizedValue = value.trim();
      if (!normalizedValue) return null;

      const timestamp = Date.parse(normalizedValue);
      return Number.isNaN(timestamp) ? null : timestamp;
    }

    if (value instanceof Date) {
      const timestamp = value.getTime();
      return Number.isNaN(timestamp) ? null : timestamp;
    }

    return null;
  }

  private resolveChatSortTimestamp(chat: ChatResponse): number {
    if (this.isGroupChat(chat)) {
      return this.parseChatTimestamp(chat.last_message_at)
        ?? Number.NEGATIVE_INFINITY;
    }

    const user1LastMessageAt = this.parseChatTimestamp(chat.user_1_last_message_at);
    const user2LastMessageAt = this.parseChatTimestamp(chat.user_2_last_message_at);

    if (this.myUserId === chat.user_1?.id) {
      return user1LastMessageAt ?? Number.NEGATIVE_INFINITY;
    }

    if (this.myUserId === chat.user_2?.id) {
      return user2LastMessageAt ?? Number.NEGATIVE_INFINITY;
    }

    return user1LastMessageAt
      ?? user2LastMessageAt
      ?? this.parseChatTimestamp(chat.updated_at)
      ?? Number.NEGATIVE_INFINITY;
  }

  private sortChatsList(chats: ChatListItem[]): ChatListItem[] {
    return chats
      .map((chat: ChatListItem, index: number): { chat: ChatListItem; index: number } => ({ chat, index }))
      .sort((left, right): number => {
        const leftTimestamp = this.resolveChatSortTimestamp(left.chat);
        const rightTimestamp = this.resolveChatSortTimestamp(right.chat);

        if (leftTimestamp !== rightTimestamp) {
          return rightTimestamp > leftTimestamp ? 1 : -1;
        }

        return left.index - right.index;
      })
      .map(({ chat }: { chat: ChatListItem }): ChatListItem => chat);
  }

  private applyChatPreviewPatch(chatId: number, patch: ChatPreviewSocketPatch): void {
    if (!chatId) return;

    const chatsList: ChatListItem[] = this.chats;
    const chatIndex: number = chatsList.findIndex((chat: ChatListItem): boolean => chat.id === chatId);

    if (chatIndex === -1) return;

    const updatedChat: ChatListItem = this.cloneChat(chatsList[chatIndex]);
    Object.assign(updatedChat, patch);
    if (this.shouldClearGroupSortFallback(updatedChat, patch)) {
      updatedChat.updated_at = undefined;
    }

    const updatedChats: ChatListItem[] = [...chatsList];
    updatedChats[chatIndex] = updatedChat;
    this.chats = this.sortChatsList(updatedChats);
  }

  private shouldClearGroupSortFallback(chat: ChatListItem, patch: ChatPreviewSocketPatch): boolean {
    return this.isGroupChat(chat)
      && Object.prototype.hasOwnProperty.call(patch, 'last_message_id')
      && Object.prototype.hasOwnProperty.call(patch, 'last_message_at')
      && patch.last_message_id === null
      && patch.last_message_at === null;
  }

  private normalizeReactionNavigationUpdatedAt(value: string | Date | null | undefined): string | undefined {
    if (typeof value === 'string') {
      const normalizedValue = value.trim();
      return normalizedValue.length > 0 ? normalizedValue : undefined;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
    }

    return undefined;
  }

  private isOlderReactionNavigationUpdate(
    currentUpdatedAt: string | Date | null | undefined,
    nextUpdatedAt: string | Date | null | undefined
  ): boolean {
    const normalizedCurrent = this.normalizeReactionNavigationUpdatedAt(currentUpdatedAt);
    const normalizedNext = this.normalizeReactionNavigationUpdatedAt(nextUpdatedAt);

    if (!normalizedCurrent || !normalizedNext) {
      return false;
    }

    return Date.parse(normalizedNext) < Date.parse(normalizedCurrent);
  }

  private resolveReactionNavigationUpdatedAt(
    currentUpdatedAt: string | Date | null | undefined,
    nextUpdatedAt: string | Date | null | undefined
  ): string | undefined {
    return this.normalizeReactionNavigationUpdatedAt(nextUpdatedAt)
      ?? this.normalizeReactionNavigationUpdatedAt(currentUpdatedAt);
  }

  private handleDataUpdatedEvent(
    data: { action?: string; chat_id?: number; message_id?: string; chat_preview?: ChatPreviewSocketPatch } | null | undefined
  ): void {
    if (data?.action !== 'edit' && data?.action !== 'delete') {
      return;
    }

    if (typeof data?.chat_id !== 'number') {
      return;
    }

    if (data.action === 'delete') {
      this.#chatComposerDraftPersistenceService.clearReplyTargetByMessageId(data.chat_id, data.message_id);
      this.#chatOutboxService.clearReplyTargetByMessageId(data.chat_id, data.message_id);
      this.#chatReplyTargetInvalidationService.notifyTargetDeleted(data.chat_id, data.message_id);
    }

    const chatPreview: ChatPreviewSocketPatch | undefined = data?.chat_preview;
    if (!chatPreview) {
      return;
    }

    this.applyChatPreviewPatch(data.chat_id, chatPreview);
  }

  private handleChatUnreadUpdatedEvent(
    data: { chat_id?: number; unread_patch?: ChatUnreadSocketPatch } | null | undefined
  ): void {
    if (typeof data?.chat_id !== 'number') {
      return;
    }

    const unreadPatch: ChatUnreadSocketPatch | undefined = data?.unread_patch;
    if (!unreadPatch) {
      return;
    }

    this.patchChatInList(data.chat_id, unreadPatch);
  }

  private handleReactionNavigationUpdated(data: ChatReactionNavigationSocketEvent | null | undefined): void {
    if (typeof data?.chat_id !== 'number') {
      return;
    }

    const currentChat = this.chats.find((chat: ChatResponse): boolean => chat.id === data.chat_id);
    const reactionNavigation = data.reaction_navigation;
    if (this.isOlderReactionNavigationUpdate(
      currentChat?.reaction_navigation_updated_at,
      reactionNavigation?.updated_at ?? null,
    )) {
      return;
    }

    this.patchChatInList(data.chat_id, {
      has_unread_reactions: reactionNavigation?.has_unread_reactions === true,
      reaction_navigation_unread_count: typeof reactionNavigation?.unread_count === 'number'
        ? reactionNavigation.unread_count
        : 0,
      reaction_navigation_updated_at: this.resolveReactionNavigationUpdatedAt(
        currentChat?.reaction_navigation_updated_at,
        reactionNavigation?.updated_at ?? null,
      ),
    });
  }

  private isGroupChat(chat: ChatResponse | undefined): boolean {
    return !!chat?.is_group;
  }

  private async handleMessageEvent(data: EventMessage): Promise<void> {
    const isProjectionUpdate = data.projection_update === true;
    const chatsList: ChatListItem[] = this.chats;
    const chatIndex: number = chatsList.findIndex((chat: ChatListItem): boolean => chat.id === data.chat_id);
    let chat: ChatListItem | undefined = chatIndex > -1 ? chatsList[chatIndex] : undefined;
    let keepCurrentListLength: boolean = false;

    if (chat) {
      this.#pendingCreatedChats.delete(data.chat_id);
    }

    if (!chat) {
      const pendingCreatedChat: ChatListItem | undefined = this.consumePendingCreatedChat(data.chat_id);
      if (pendingCreatedChat) {
        if (!this.matchesCurrentFilters(pendingCreatedChat)) return;

        chat = pendingCreatedChat;
        keepCurrentListLength = true;
      }
    }

    if (!chat) {
      try {
        const response = await firstValueFrom(this.#client.chatGet(data.chat_id, undefined));
        const chatData: ChatListItem | undefined = response?.data ? this.cloneChat(response.data) : undefined;

        if (!chatData || !this.matchesCurrentFilters(chatData)) return;

        try {
          const reactionNavigationResponse = await firstValueFrom(this.#client.chatReactionNavigation(data.chat_id));
          const reactionNavigation = reactionNavigationResponse?.data;
          chatData.has_unread_reactions = reactionNavigation?.has_unread_reactions === true;
          chatData.reaction_navigation_unread_count = typeof reactionNavigation?.unread_count === 'number'
            ? reactionNavigation.unread_count
            : 0;
          chatData.reaction_navigation_updated_at = this.resolveReactionNavigationUpdatedAt(
            chatData.reaction_navigation_updated_at,
            reactionNavigation?.updated_at ?? null,
          );
        } catch (reactionNavigationError) {
          console.error('Error while fetching reaction navigation by chat id', reactionNavigationError);
        }

        chat = chatData;
        keepCurrentListLength = true;
      } catch (error) {
        console.error('Error while fetching chat by id', error);
        this.markNeedChatsReload();
        return;
      }
    }

    const isTargetedSystemMessage: boolean = (data.type === MESSAGE_TYPE.MESSAGE_SYSTEM) && (data?.to_id != null) && (data.to_id !== this.myUserId);
    if (isTargetedSystemMessage) return;

    const incomingTimestamp = this.parseChatTimestamp(data.created_at);
    if (
      isProjectionUpdate
      && incomingTimestamp !== null
      && this.resolveChatSortTimestamp(chat) >= incomingTimestamp
    ) {
      return;
    }

    const previewResult = buildChatEventPreview(data);
    const lastMsg = previewResult.preview.raw;
    const isCallServiceMessage = previewResult.isCallServiceMessage;
    const isForwardedMessage = previewResult.isForwardedMessage;

    if (this.isGroupChat(chat)) {
      const updatedChat: ChatListItem = this.cloneChat(chat);

      updatedChat.last_message_text = lastMsg;
      updatedChat.last_message_at = data.created_at;
      updatedChat.updated_at = data.created_at;
      updatedChat.last_message_user = data?.user_info
        ? UserShortResponse.fromJS(data.user_info)
        : null;

      if (data.from_id !== this.myUserId && !isCallServiceMessage && !isForwardedMessage) {
        const currentUnread: number = typeof updatedChat.my_unread_count === 'number' ? updatedChat.my_unread_count : 0;
        updatedChat.my_unread_count = currentUnread + (isProjectionUpdate ? 0 : 1);
      }

      this.upsertChatToTop(updatedChat, keepCurrentListLength, isProjectionUpdate);
      return;
    }

    const isMyUser1: boolean = this.myUserId === chat.user_1.id;
    const isMyUser2: boolean = this.myUserId === chat.user_2.id;
    const updatedChat: ChatListItem = this.cloneChat(chat);

    if (isMyUser1 || isMyUser2) {
      const shouldIncreaseUnread: boolean = !isCallServiceMessage
        && !isProjectionUpdate
        && (data.from_id !== this.myUserId)
        && !isForwardedMessage;

      if (isMyUser1) {
        updatedChat.user_1_last_message = lastMsg;
        updatedChat.user_1_last_message_at = data.created_at;
        if (shouldIncreaseUnread) updatedChat.user_1_qty_unread_messages++;
      } else {
        updatedChat.user_2_last_message = lastMsg;
        updatedChat.user_2_last_message_at = data.created_at;
        if (shouldIncreaseUnread) updatedChat.user_2_qty_unread_messages++;
      }
    }

    updatedChat.updated_at = data.created_at;
    this.upsertChatToTop(updatedChat, keepCurrentListLength, isProjectionUpdate);
  }

  private upsertChatToTop(
    chat: ChatListItem,
    keepCurrentListLength: boolean,
    sortByTimestamp: boolean = false,
  ): void {
    const currentChats: ChatListItem[] = [...this.chats];
    const existingIndex: number = currentChats.findIndex((item: ChatListItem): boolean => item.id === chat.id);

    if (existingIndex > -1) {
      currentChats.splice(existingIndex, 1);
    }

    const previousLength: number = currentChats.length;
    let nextChats: ChatListItem[] = [chat, ...currentChats];
    if (sortByTimestamp) {
      nextChats = this.sortChatsList(nextChats);
    }

    if (keepCurrentListLength && existingIndex === -1 && previousLength > 0 && nextChats.length > previousLength) {
      nextChats.length = previousLength;
    }

    this.chats = nextChats;
  }

  private matchesCurrentFilters(chat: ChatResponse): boolean {
    return this.matchesSelectedChatType(chat) && this.matchesSelectedChatStatus(chat);
  }

  private matchesSelectedChatType(chat: ChatResponse): boolean {
    const hasReply: boolean = !!chat?.reply_id || !!chat?.reply;
    const hasOffer: boolean = !!chat?.offer_id || !!chat?.offer;
    const isFreelanceChat: boolean = (chat?.type === CHAT_TYPE.FREELANCE) || hasReply || hasOffer || !!chat?.task_id;
    const isUserChat: boolean = (chat?.type === CHAT_TYPE.USER) || (!hasReply && !hasOffer && !chat?.task_id);

    switch (this.selectedChatType) {
      case CHAT_TYPE.ALL:
        return true;
      case CHAT_TYPE.REPLY:
        return hasReply;
      case CHAT_TYPE.OFFER:
        return hasOffer;
      case CHAT_TYPE.FREELANCE:
        return isFreelanceChat;
      case CHAT_TYPE.USER:
        return isUserChat;
      default:
        return true;
    }
  }

  private matchesSelectedChatStatus(chat: ChatResponse): boolean {
    const replyStatusRaw: number | undefined = chat?.reply?.status;

    switch (this.selectedChatStatusSegment) {
      case FREELANCE_CHAT_STATUS.PUBLISHED:
        if (replyStatusRaw == null) return true;
        return [ReplyStatus.PENDING, ReplyStatus.REFRESH].includes(replyStatusRaw);
      case FREELANCE_CHAT_STATUS.WORK:
        return [
          ReplyStatus.ACCEPTED,
          ReplyStatus.COMPLITED_BY_WORKER,
          ReplyStatus.EXTRA_WORK,
          ReplyStatus.DISPUTED,
          ReplyStatus.EXTRA_WORK_WAIT_PAY,
          ReplyStatus.EXTRA_WORK_CREATED,
          ReplyStatus.EXTRA_WORK_REJECTED,
        ].includes(replyStatusRaw);
      case FREELANCE_CHAT_STATUS.DONE:
        return [
          ReplyStatus.REJECTED,
          ReplyStatus.ACCEPTED_BY_OWNER,
          ReplyStatus.DISPUTE_CLOSED_IN_FAVOR_OWNER,
          ReplyStatus.DISPUTE_CLOSED_IN_FAVOR_WORKER,
          ReplyStatus.DELETED,
          ReplyStatus.DISPUTE_SOLVED,
        ].includes(replyStatusRaw);
      default:
        return true;
    }
  }

  private handleReplyStatusChanged(data: EventReplyStatusChanged): void {
    if (!this.chats.some((chat: ChatResponse): boolean => chat.id === data.chatId)) return;

    const {replyStatus} = data;
    const status: FREELANCE_CHAT_STATUS = this.selectedChatStatusSegment;

    const shouldReload: boolean =
      (replyStatus === ReplyStatus.ACCEPTED && [FREELANCE_CHAT_STATUS.PUBLISHED, FREELANCE_CHAT_STATUS.WORK].includes(status))
      || (replyStatus === ReplyStatus.REJECTED && [FREELANCE_CHAT_STATUS.PUBLISHED, FREELANCE_CHAT_STATUS.DONE].includes(status))
      || ([ReplyStatus.ACCEPTED_BY_OWNER, ReplyStatus.DISPUTE_SOLVED].includes(replyStatus) && [FREELANCE_CHAT_STATUS.WORK, FREELANCE_CHAT_STATUS.DONE].includes(status));

    if (shouldReload) {
      this.markNeedChatsReload();
    }
  }

  private handleReadEvent(data: EventRead): void {
    const {chat_id, readBy} = data;

    const chatToUpdate: ChatResponse = this.chats.find(c => c.id === chat_id);
    if (!chatToUpdate) return;

    if (this.isGroupChat(chatToUpdate)) {
      if (this.myUserId === readBy) {
        chatToUpdate.my_unread_count = 0;
      }
      return;
    }

    if (this.myUserId === chatToUpdate.user_2.id && readBy === chatToUpdate.user_2.id) {
      chatToUpdate.user_2_qty_unread_messages = 0;
    } else if (this.myUserId === chatToUpdate.user_1.id && readBy === chatToUpdate.user_1.id) {
      chatToUpdate.user_1_qty_unread_messages = 0;
    }
  }

  private handleQtyUnreadMessages(data: QtyUnreadMessages): void {
    this.unreadMessages = data; // if device, the update additionally takes place from the backend endpoint 'chatQtyUnreadMessages' in app.component.ts
  }

  private handleChatCreated(data: EventChatCreated): void {
    if (this.selectedChatStatusSegment === FREELANCE_CHAT_STATUS.PUBLISHED) {
      this.markNeedChatsReload();
    }
  }

  private markNeedChatsReload(): void {
    this.#reloadChatsSubject.next();
  }
}
